// When a parent app embeds Elis in an iframe and drives autologin via
// `?auto_login_sso_provider_id=N`, multiple parents share the same Elis origin
// but map to different Rossum orgs/users. To keep their sessions from clashing
// in the shared `localStorage` we pin the provider id for THIS tab in
// `sessionStorage` (tab-scoped) and namespace the auth token key by it (see
// `token.ts`). The parent's `?user_id=N` is pinned alongside it, because
// several parent users can share one provider (MAT-2347). A fresh tab re-derives the provider from its own URL, so the pin
// never leaks across tabs while the token itself stays in `localStorage` and
// survives restarts.

const PROVIDER_PIN_KEY = 'auto_login_sso_provider_id';
const USER_PIN_KEY = 'user_id';

export const isIframeEmbedded = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    // Accessing `window.top` across origins can throw — which only happens when
    // we are framed by a different-origin parent.
    return true;
  }
};

const pin = (key: string, value: string | null) =>
  value ? sessionStorage.setItem(key, value) : sessionStorage.removeItem(key);

/**
 * Pin the autologin SSO provider id and the parent's user id for this tab when
 * embedded via an iframe and the parent supplied either. Run once at startup,
 * before any auth token is read.
 */
export const initEmbeddedSsoSession = (): void => {
  if (!isIframeEmbedded()) {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const providerId = params.get(PROVIDER_PIN_KEY);
  const userId = params.get(USER_PIN_KEY);

  if (!providerId && !userId) {
    return;
  }

  pin(PROVIDER_PIN_KEY, providerId);
  pin(USER_PIN_KEY, userId);
};

export const getActiveSsoProviderId = (): string | null =>
  sessionStorage.getItem(PROVIDER_PIN_KEY);

export const getActiveSsoUserId = (): string | null =>
  sessionStorage.getItem(USER_PIN_KEY);
